import { useCallback, useEffect, useMemo, useState } from 'react';
import { persistentStorage, type PersistentStorageKey } from '../storage';
import { trackGoal } from '../trackGoal';

export type AdvLogicProps = {
    // По этому ключу в localStorage будет записана информация о показах
    id: PersistentStorageKey;
    // Начало показа баннера
    startTime: number;
    // Конец показа баннера
    endTime: number;
    // Сколько раз закрываем, прежде, чем прекратим
    times: number;
    // Логика определения перерыва между показами
    getTimeout: (timesClosed: number) => number;
};

type AdvLogic = {
    // Нужно ли отображать баннер
    isOpen: boolean;
    // Колбэк, нужно вызвать при показе.
    onShow: VoidFunction;
    // Колбэк, нужно вызвать при клике, если хотим вызов счётчика по клику
    onClick: VoidFunction;
    // Колбэк, нужно вызвать при закрытии (если оно есть).
    onClose: VoidFunction;
    // Сколько раз закрыли
    timesClosed: number;
};

// Сохраняем в localStorage
type AdvInfo = {
    // id баннера
    id: PersistentStorageKey;
    // Сколько раз уже закрыли баннер
    timesClosed: number;
    // Когда в последний раз показали баннер
    lastClosed?: number;
};

// Тип для данных баннеров в хранилище
type AdvInfoData = Map<PersistentStorageKey, AdvInfo | undefined>;

// Тип для callback'ов подписчиков
type SubscriberCallback = () => void;

// Тип для множества подписчиков
type SubscriberSet = Set<SubscriberCallback>;

// Тип для подписчиков на изменения
type AdvInfoSubscribers = Map<PersistentStorageKey, SubscriberSet>;

// Глобальное хранилище для синхронизации состояния между всеми вызовами хука
type AdvInfoStore = {
    data: AdvInfoData;
    subscribers: AdvInfoSubscribers;
};

const store: AdvInfoStore = {
    data: new Map<PersistentStorageKey, AdvInfo | undefined>(),
    subscribers: new Map<PersistentStorageKey, SubscriberSet>(),
};

// Функция для получения данных из хранилища
const getAdvInfo = (id: PersistentStorageKey): AdvInfo | undefined => {
    if (!store.data.has(id)) {
        const advInfoFromStorage = persistentStorage.getItem(id);
        const advInfo = advInfoFromStorage ? JSON.parse(advInfoFromStorage) : undefined;

        store.data.set(id, advInfo);
    }

    return store.data.get(id);
};

// Функция для обновления данных в хранилище
const updateAdvInfo = (id: PersistentStorageKey, advInfo: AdvInfo | undefined): void => {
    store.data.set(id, advInfo);

    const oldAdvInfo = getAdvInfo(id);

    if (advInfo) {
        persistentStorage.setItem(id, JSON.stringify({ ...oldAdvInfo, ...advInfo }));
    }

    // Уведомляем всех подписчиков
    const subscribers = store.subscribers.get(id);

    if (subscribers) {
        subscribers.forEach((callback) => callback());
    }
};

// Функция для подписки на изменения
const subscribe = (id: PersistentStorageKey, callback: SubscriberCallback): (() => void) => {
    if (!store.subscribers.has(id)) {
        store.subscribers.set(id, new Set<SubscriberCallback>());
    }

    store.subscribers.get(id)?.add(callback);

    // Возвращаем функцию отписки
    return () => {
        const subscribers = store.subscribers.get(id);

        if (subscribers) {
            subscribers.delete(callback);

            if (subscribers.size === 0) {
                store.subscribers.delete(id);
            }
        }
    };
};

export const useAdvLogic = (props: AdvLogicProps): AdvLogic => {
    const { id, startTime, endTime, times, getTimeout } = props;
    // Используем глобальное хранилище с подпиской на изменения
    const [advInfo, setAdvInfo] = useState<AdvInfo | undefined>(() => getAdvInfo(id));

    // Подписываемся на изменения для этого id
    useEffect(() => {
        const unsubscribe = subscribe(id, () => {
            setAdvInfo(getAdvInfo(id));
        });

        return unsubscribe;
    }, [id]);

    // Обновляем состояние при изменении id
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- Автоматический игнор в рамках DIRECT-312943
        setAdvInfo(getAdvInfo(id));
    }, [id]);

    const isOpen = useMemo(() => {
        const now = new Date().getTime();

        // Если рекламная компания ещё не началась или уже закончилась
        if (now < startTime || now > endTime) {
            return false;
        }

        // Если показываем ноль раз
        if (times === 0) {
            return false;
        }

        // Если цель по закрытиям была выполнена
        if (advInfo && advInfo.timesClosed >= times) {
            return false;
        }

        // Проверяем, прошло ли достаточно времени с последнего показа
        if (advInfo && advInfo.lastClosed && now - advInfo.lastClosed < getTimeout(advInfo.timesClosed)) {
            return false;
        }

        return true;
    }, [times, getTimeout, startTime, endTime, advInfo]);

    const onShow = useCallback(() => {
        const currentAdvInfo = getAdvInfo(id);
        const newAdvInfo: AdvInfo = {
            id,
            timesClosed: currentAdvInfo?.timesClosed ?? 0,
        };

        updateAdvInfo(id, newAdvInfo);
        trackGoal(`${id}_show`);
    }, [id]);

    const onClose = useCallback(() => {
        const currentAdvInfo = getAdvInfo(id);
        const now = new Date().getTime();
        const newAdvInfo: AdvInfo = {
            id,
            timesClosed: (currentAdvInfo?.timesClosed ?? 0) + 1,
            lastClosed: now,
        };

        updateAdvInfo(id, newAdvInfo);
        trackGoal(`${id}_close`);
    }, [id]);

    const onClick = useCallback(() => {
        trackGoal(`${id}_click`);
    }, [id]);

    return {
        isOpen,
        onShow,
        onClick,
        onClose,
        timesClosed: advInfo?.timesClosed ?? 0,
    };
};
